import React from 'react';
import { Clock, DollarSign, CheckCircle, MapPin } from 'lucide-react';

const Tarifs = () => {
  const tarifs = [
    {
      id: 1,
      title: "Changement pansement simple",
      duration: "30 min",
      price: "30 $",
      location: "Chez le client",
      accent: "accent-blue",
      description: [
        "Changement de pansement standard",
        "Nettoyage et désinfection de la plaie", 
        "Application du nouveau pansement",
        "Conseils pour l'entretien"
      ]
    },
    {
      id: 2,
      title: "Changement pansement complexe", 
      duration: "1 h",
      price: "75 $",
      location: "Chez le client",
      accent: "accent-pink",
      description: [
        "Pansements spécialisés avancés",
        "Évaluation approfondie de la plaie",
        "Techniques de soins complexes",
        "Suivi personnalisé de la cicatrisation"
      ]
    },
    {
      id: 3,
      title: "Retrait d'agrafes ou de sutures",
      duration: "30 min", 
      price: "70 $",
      location: "Chez le client",
      accent: "accent-green",
      description: [
        "Évaluation de la cicatrisation",
        "Retrait sécuritaire des agrafes/sutures",
        "Désinfection de la zone",
        "Conseils post-retrait"
      ]
    },
    {
      id: 4,
      title: "Injection sous-cutanée/intra-musculaire",
      duration: "15 min",
      price: "25 $",
      location: "Chez le client",
      accent: "accent-cream",
      description: [
        "Administration sécuritaire d'injections",
        "Respect des protocoles médicaux",
        "Matériel stérilisé fourni",
        "Surveillance post-injection"
      ]
    },
    {
      id: 5,
      title: "Soins de sonde urinaire",
      duration: "45 min",
      price: "70 $", 
      location: "Chez le client",
      accent: "accent-blue",
      description: [
        "Changement de sonde urinaire",
        "Nettoyage et désinfection",
        "Vérification du bon fonctionnement",
        "Éducation sur l'entretien"
      ]
    },
    {
      id: 6,
      title: "Soins de stomie",
      duration: "1 h",
      price: "70 $",
      location: "Chez le client",
      accent: "accent-pink",
      description: [
        "Changement du sac de stomie",
        "Nettoyage de la peau péristomiale",
        "Évaluation de l'état de la stomie",
        "Enseignement des soins d'hygiène"
      ]
    },
    {
      id: 7,
      title: "Prise de signes vitaux",
      duration: "15 min",
      price: "20 $",
      location: "Chez le client",
      accent: "accent-green",
      description: [
        "Mesure de la tension artérielle",
        "Prise de température corporelle",
        "Évaluation du pouls et de la respiration",
        "Rapport détaillé des résultats"
      ]
    },
    {
      id: 8,
      title: "Prise de sang",
      duration: "15 min",
      price: "À partir de 95 $",
      location: "Chez le client",
      accent: "accent-cream",
      special: true,
      availability: "Vendredi 6h-11h00 (à venir)",
      requirement: "Prescription obligatoire",
      description: [
        "Frais de déplacement inclus",
        "Frais supplémentaires pour les tests exceptionnels", 
        "Possibilité de faire une soumission",
        "Résultats transmis à votre médecin"
      ]
    }
  ];

  const soinsPodologiques = [
    "Amincissement des ongles",
    "Coupe des ongles", 
    "Dégagement des sillons",
    "Retrait de la kératose",
    "Retrait des callosités, fissures/crevasses",
    "Retrait des cors, des durillons",
    "Pieds diabétiques",
    "Soins ongle incarné"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 pt-24">
      <div className="container mx-auto px-4 py-12">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="heading-1 mb-6">
            Nos <span className="text-gradient">Tarifs</span>
          </h1>
          <p className="body-large text-gray-600 max-w-3xl mx-auto">
            Découvrez nos tarifs transparents pour tous nos services de soins à domicile. 
            Tarification claire et juste pour des soins de qualité professionnelle.
          </p>
        </div>

        {/* Services Généraux */}
        <div className="mb-16">
          <h2 className="heading-2 text-center mb-12">Services de soins</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tarifs.map((tarif) => (
              <div key={tarif.id} className={`service-card ${tarif.accent} hover-lift`}>
                
                {/* Header */}
                <div className="text-center mb-6">
                  <h3 className="heading-3 mb-4">{tarif.title}</h3>
                  {tarif.special && (
                    <div className="space-y-2">
                      <p className="body-small text-orange-600 font-medium">{tarif.availability}</p>
                      <p className="body-small text-red-600 font-medium">{tarif.requirement}</p>
                    </div>
                  )}
                </div>

                {/* Pricing Info */}
                <div className="space-y-4 mb-6">
                  
                  {/* Duration */}
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Clock size={20} className="text-blue-600" />
                      <span className="body-small font-medium">Durée</span>
                    </div>
                    <span className="body-small font-semibold">{tarif.duration}</span>
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <DollarSign size={20} className="text-green-600" />
                      <span className="body-small font-medium">Prix</span>
                    </div>
                    <span className="body-small font-semibold text-green-700">{tarif.price}</span>
                  </div>

                  {/* Location */}
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <MapPin size={20} className="text-purple-600" />
                      <span className="body-small font-medium">Lieu</span>
                    </div>
                    <span className="body-small font-semibold">{tarif.location}</span>
                  </div>

                </div>

                {/* Description */}
                <div className="mb-6">
                  <h4 className="body-medium font-semibold mb-3">Description du service :</h4>
                  <ul className="space-y-2">
                    {tarif.description.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="body-small text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Politique d'annulation */}
                <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="body-small font-semibold text-yellow-800 mb-2">Politique d'annulation</h4>
                  <p className="body-small text-yellow-700">
                    Pour annuler ou reprogrammer merci de nous contacter 24h à l'avance
                  </p>
                </div>

                {/* CTA */}
                <div className="text-center">
                  <a 
                    href="/contact" 
                    className="btn-primary w-full"
                  >
                    Prendre rendez-vous
                  </a>
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* Soins Podologiques Section */}
        <div className="service-card accent-cream max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="heading-2 mb-4">Soins podologiques</h2>
            <div className="space-y-2">
              <p className="body-small text-gray-700">
                <strong>Avec ou sans prescription</strong> des frais de <strong>15$</strong> s'appliquent à l'ouverture de dossier
              </p>
              <div className="flex items-center justify-center space-x-4 bg-white/60 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <DollarSign size={24} className="text-green-600" />
                  <span className="heading-3 text-green-700">75 $ / 1h</span>
                </div>
                <span className="body-small text-gray-500">|</span>
                <div className="flex items-center space-x-2">
                  <MapPin size={20} className="text-purple-600" />
                  <span className="body-medium">Chez le client</span>
                </div>
              </div>
            </div>
          </div>

          {/* Services Description */}
          <div className="mb-8">
            <h3 className="heading-3 mb-6 text-center">Description des services</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {soinsPodologiques.map((service, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-white/50 rounded-lg">
                  <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                  <span className="body-small text-gray-700">{service}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <a 
              href="/contact" 
              className="btn-primary inline-flex items-center space-x-2"
            >
              <DollarSign size={20} />
              <span>Prendre rendez-vous pour soins podologiques</span>
            </a>
          </div>

        </div>

        {/* Note Section */}
        <div className="text-center mt-12 p-6 bg-blue-50 rounded-lg max-w-2xl mx-auto">
          <p className="body-small text-gray-600">
            <strong>Note :</strong> Tous nos tarifs sont en dollars canadiens. 
            Les frais de déplacement sont inclus dans la région de Longueuil. 
            Pour toute question sur nos tarifs, n'hésitez pas à nous contacter.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Tarifs;